import { useEffect, useState } from 'react'
import { Leaf, Rocket, Diamond, BarChart3, Zap, CheckCircle, Info, AlertCircle, Clock } from 'lucide-react'

interface SubscriptionPlan {
  id: string
  name: string
  displayName: string
  priceMonthly: number
  priceQuarterly?: number
  referralRequestsLimitMonthly: number
  referralRequestsLimitQuarterly?: number
  featuresMonthly: string[]
  featuresQuarterly: string[]
  stripePriceIdMonthly?: string
  stripePriceIdQuarterly?: string
}

import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'

export default function SubscriptionPage() {
  const { user, getToken } = useAuth()
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [currentSubscription, setCurrentSubscription] = useState<any>(null)
  const [usage, setUsage] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'quarterly'>('monthly')
  const [error, setError] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0 })

  useEffect(() => {
    if (user?.type === 'jobseeker') {
      fetchPlans()
      fetchCurrentSubscription()
    }
  }, [user])

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const SALE_CYCLE_DAYS = 5
      const REFERENCE_DATE = new Date('2025-01-01T00:00:00Z').getTime()
      
      const now = Date.now()
      const timeSinceReference = now - REFERENCE_DATE
      const cycleMs = SALE_CYCLE_DAYS * 24 * 60 * 60 * 1000
      
      const currentCycleElapsed = timeSinceReference % cycleMs
      const timeUntilNextCycle = cycleMs - currentCycleElapsed
      
      const days = Math.floor(timeUntilNextCycle / (24 * 60 * 60 * 1000))
      const hours = Math.floor((timeUntilNextCycle % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
      const minutes = Math.floor((timeUntilNextCycle % (60 * 60 * 1000)) / (60 * 1000))
      
      setTimeRemaining({ days, hours, minutes })
    }

    calculateTimeRemaining()
    const interval = setInterval(calculateTimeRemaining, 60000)
    
    return () => clearInterval(interval)
  }, [])

  const fetchPlans = async () => {
    try {
      const token = await getToken()
      if (!token) return

      const response = await api.getSubscriptionPlans(token)
      if (response.success) {
        setPlans(response.plans)
      }
    } catch (error) {
      console.error('Error fetching plans:', error)
      setError('Failed to load subscription plans')
    }
  }

  const fetchCurrentSubscription = async () => {
    try {
      const token = await getToken()
      if (!token) return

      const response = await api.getCurrentSubscription(token)
      if (response.success) {
        setCurrentSubscription(response.subscription)
        setUsage(response.usage)
      }
    } catch (error) {
      console.error('Error fetching subscription:', error)
    }
  }

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    try {
      setIsLoading(true)
      setError(null)
      
      const token = await getToken()
      if (!token) {
        setError('Please log in to continue')
        return
      }

      const response = await api.createCheckoutSession(plan.id, selectedBilling, token)
      if (response.success && response.sessionUrl) {
        window.location.href = response.sessionUrl
      } else {
        throw new Error(response.error || 'Failed to create checkout session')
      }
    } catch (error) {
      console.error('Error creating checkout:', error)
      setError('Failed to start checkout. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelSubscription = () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.')) {
      cancelSubscription()
    }
  }

  const cancelSubscription = async () => {
    try {
      const token = await getToken()
      if (!token) return

      const response = await api.cancelSubscription(false, token)
      if (response.success) {
        alert('Your subscription will be canceled at the end of your current billing period.')
        await fetchCurrentSubscription()
      } else {
        throw new Error(response.error || 'Failed to cancel subscription')
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      alert('Failed to cancel subscription')
    }
  }

  const getPlanIcon = (planName: string) => {
    switch (planName) {
      case 'free': return Leaf
      case 'basic': return Rocket
      case 'premium': return Diamond
      default: return Rocket
    }
  }

  const getPlanColors = (planName: string) => {
    switch (planName) {
      case 'free': return { from: 'from-green-400', to: 'to-green-600', text: 'text-green-500', bg: 'bg-green-500' }
      case 'basic': return { from: 'from-blue-400', to: 'to-blue-600', text: 'text-blue-500', bg: 'bg-blue-500' }
      case 'premium': return { from: 'from-amber-400', to: 'to-amber-600', text: 'text-amber-500', bg: 'bg-amber-500' }
      default: return { from: 'from-gray-400', to: 'to-gray-600', text: 'text-gray-500', bg: 'bg-gray-500' }
    }
  }

  if (user?.type !== 'jobseeker') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Info className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Referrers Get Free Access</h2>
          <p className="text-gray-600">
            As a referrer, you have unlimited access to all features for free. 
            Subscriptions are only required for job seekers.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-6">Subscription</h1>
      
      {/* Current Usage Card */}
      {usage && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="h-6 w-6 text-blue-500" />
            <h2 className="ml-2 text-lg font-semibold text-gray-900">Current Usage</h2>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{usage.requestsUsedThisMonth}</p>
              <p className="text-sm text-gray-600 mt-1">Used</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{usage.requestsRemaining}</p>
              <p className="text-sm text-gray-600 mt-1">Remaining</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{usage.referralRequestsLimit}</p>
              <p className="text-sm text-gray-600 mt-1">Total</p>
            </div>
          </div>

          {currentSubscription?.currentPeriodEnd && (
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">
                {currentSubscription.cancelAtPeriodEnd ? 'Cancels on' : 'Renews on'}{' '}
                <span className="font-medium text-gray-900">{new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}</span>
              </p>
            </div>
          )}

          {usage.isPriority && (
            <div className="flex items-center justify-center bg-amber-50 rounded-lg p-3">
              <Zap className="h-4 w-4 text-amber-500" />
              <span className="ml-2 text-sm font-medium text-amber-900">Priority Matching Enabled</span>
            </div>
          )}
        </div>
      )}

      {/* Billing Toggle */}
      <div className="flex items-center justify-center mb-6">
        <div className="bg-white rounded-lg p-1 shadow-sm inline-flex">
          <button
            onClick={() => setSelectedBilling('monthly')}
            className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
              selectedBilling === 'monthly'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedBilling('quarterly')}
            className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
              selectedBilling === 'quarterly'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Quarterly
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => {
          const isCurrentPlan = currentSubscription?.planId === plan.id && 
                               currentSubscription?.billingCycle === selectedBilling
          const hasActivePremiumSubscription = currentSubscription?.planName === 'premium' && 
                                               currentSubscription?.status === 'active' &&
                                               !currentSubscription?.cancelAtPeriodEnd
          const colors = getPlanColors(plan.name)
          const Icon = getPlanIcon(plan.name)
          const isPremium = plan.name === 'premium'
          const actualPrice = selectedBilling === 'monthly' ? plan.priceMonthly : (plan.priceQuarterly || plan.priceMonthly * 3)
          const displayOriginalPrice = isPremium ? actualPrice * 2 : actualPrice
          const salePrice = actualPrice
          const price = salePrice
          const savings = plan.priceQuarterly ? Math.round(((plan.priceMonthly * 3) - plan.priceQuarterly) / (plan.priceMonthly * 3) * 100) : 0

          return (
            <div
              key={plan.id}
              className={`bg-white rounded-xl shadow-sm overflow-hidden ${
                isCurrentPlan ? 'ring-2 ring-blue-500' : ''
              } ${isPremium ? 'ring-2 ring-orange-500' : ''}`}
            >
              {/* Sale Timer Badge - Premium Only */}
              {isPremium && (
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm font-bold">
                    <Clock className="h-4 w-4" />
                    <span>🔥 Sale ends in {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m</span>
                  </div>
                </div>
              )}

              {/* Header */}
              <div className={`bg-gradient-to-r ${colors.from} ${colors.to} p-6 text-white`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Icon className="h-6 w-6" />
                    <h3 className="ml-2 text-xl font-bold">{plan.displayName}</h3>
                  </div>
                  {isCurrentPlan && (
                    <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs font-medium">
                      Current
                    </span>
                  )}
                  {isPremium && !isCurrentPlan && (
                    <span className="bg-red-500 px-2 py-1 rounded-full text-xs font-bold">
                      50% OFF
                    </span>
                  )}
                </div>
                
                {isPremium ? (
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-medium line-through opacity-70">${displayOriginalPrice.toFixed(2)}</span>
                      <span className="text-4xl font-bold">${salePrice.toFixed(2)}</span>
                      <span className="ml-1 text-white text-opacity-80">/{selectedBilling}</span>
                    </div>
                    <p className="text-sm text-white font-semibold mt-1">Limited Time Offer!</p>
                  </div>
                ) : (
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">${price.toFixed(2)}</span>
                    <span className="ml-1 text-white text-opacity-80">/{selectedBilling}</span>
                  </div>
                )}
                
                {selectedBilling === 'quarterly' && savings > 0 && !isPremium && (
                  <p className="text-sm text-white text-opacity-90 mt-1">Save {savings}%</p>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className={`flex items-center bg-gray-50 rounded-lg p-3 mb-4`}>
                  <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    {(() => {
                      const limit = selectedBilling === 'quarterly' 
                        ? plan.referralRequestsLimitQuarterly || plan.referralRequestsLimitMonthly
                        : plan.referralRequestsLimitMonthly;
                      
                      if (limit === 0) {
                        return 'No referral requests';
                      } else if (limit === 50) {
                        return selectedBilling === 'quarterly' 
                          ? '50 referral requests/quarter' 
                          : '50 referral requests/month';
                      } else {
                        return selectedBilling === 'quarterly'
                          ? `${limit} referral requests/quarter`
                          : `${limit} referral requests/month`;
                      }
                    })()}
                  </span>
                </div>

                <div className="space-y-2 mb-6">
                  {(selectedBilling === 'quarterly' ? plan.featuresQuarterly : plan.featuresMonthly).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className={`h-4 w-4 ${colors.text} flex-shrink-0 mt-0.5`} />
                      <span className="ml-2 text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>

                {!isCurrentPlan && 
                 plan.name !== 'free' && 
                 plan.name !== 'basic' && 
                 !(plan.name === 'premium' && hasActivePremiumSubscription) &&
                 (plan.stripePriceIdMonthly || plan.stripePriceIdQuarterly) && (
                  <button
                    onClick={() => handleSubscribe(plan)}
                    disabled={isLoading}
                    className={`w-full py-3 rounded-lg font-medium text-white transition-colors ${colors.bg} hover:opacity-90 disabled:opacity-50`}
                  >
                    {isLoading ? 'Loading...' : 'Choose Plan'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Current Subscription Management */}
      {currentSubscription && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Subscription</h2>
          
          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Plan:</span> {currentSubscription.planDisplayName || currentSubscription.planName || 'Unknown Plan'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Status:</span> {currentSubscription.status}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Billing:</span> {currentSubscription.billingCycle}
            </p>
            {currentSubscription.currentPeriodEnd && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">
                  {currentSubscription.cancelAtPeriodEnd ? 'Cancels on:' : 'Next billing:'}
                </span> {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
              </p>
            )}
          </div>

          {currentSubscription.cancelAtPeriodEnd && (
            <div className="flex items-start bg-amber-50 rounded-lg p-4 mb-4">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="ml-3 text-sm text-amber-900">
                Your subscription is scheduled to cancel at the end of your current billing period. 
                You'll continue to have access to premium features until then.
              </p>
            </div>
          )}

          {currentSubscription.planName !== 'basic' && !currentSubscription.cancelAtPeriodEnd && (
            <button
              onClick={handleCancelSubscription}
              className="w-full px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
            >
              Cancel Subscription
            </button>
          )}

          {currentSubscription.planName === 'basic' && (
            <div className="flex items-start bg-blue-50 rounded-lg p-4">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="ml-3 text-sm text-blue-900">
                Basic plan is free. Upgrade to premium for additional features.
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      </div>
    </div>
  )
}
